<template>
  <div class="p-5 pt-2">
    <!-- 表格数据 -->
    <el-table class="normal-table" :style="{ maxHeight: '300px', borderBottom: isNull(tableData) || tableData.length > 0 ? 'none' : '1px solid var(--color-split-line) !important'} " :border="true" :data="tableData">
      <el-table-column align="left" prop="type" label="审批类型" :min-width="200">
        <template #default="scope">
          {{ approveTypeLabel[scope.row.type] }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="submitTime" show-overflow-tooltip label="提交时间" :min-width="180" />
      <el-table-column align="left" prop="submitter" label="提交人" :min-width="150" />
      <el-table-column v-if="!isCompanyIdentity" align="left" show-overflow-tooltip prop="companyName" label="业务所属公司" :min-width="260" :formatter="(row) => isNull(row.companyName) ? '--' : row.companyName" />
      <el-table-column v-permission="['approval:manage:examine']" align="center" fixed="right" prop="taskNo" label="操作" :min-width="80">
        <template #default="scope">
          <el-button link type="primary" @click="openApproverConfig(scope.row)">
            审批
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, PropType, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { IApproveManageParam, IApproveTaskRes } from '@core/main/types/system/approval';
import { approveTypeLabel, EApproveStatus, EApproveType, IBusinessLineItem } from '@core/main/types/common';
import { useRoute, useRouter } from 'vue-router';
import { isNull } from '@core/tools';
import { queryBusinessLineList, queryApproveTaskList } from '@core/main/api';

const props = defineProps({
  isCompanyIdentity: {
    type: Boolean,
    default: false,
  },
  pageParams: {
    type: Object as PropType<{ page: number; size: number }>,
    default: () => ({
      page: 1,
      size: 6,
    }),
  },
});

const emit = defineEmits(['update:total', 'update:loading']);

const router = useRouter();
const route = useRoute();

const params = reactive<IApproveManageParam>({
  page: 1,
  size: props.pageParams.size,
  statuses: `${EApproveStatus.PENDING}`,
});

const total = ref(0);
const tableData = ref<IApproveTaskRes[]>([]);
const businessLineList = ref<IBusinessLineItem[]>([]);
const dateRange = ref<string[]>([]);
const actinveIndex = ref(route.query && route.query.actinveIndex ? Number(route.query.actinveIndex) : 1);

function openApproverConfig(row: IApproveTaskRes) {
  router.push({
    name: 'ApprovalForm',
    query: {
      taskNo: row.taskNo,
      type: row.type,
      refId: row.type === EApproveType.APPLET_PUB ? row.agentId : row.refId,
      companyId: row.type === EApproveType.TAX_SOURCE || row.type === EApproveType.EDIT_TAX_SOURCE ? row.companyId : undefined,
      serverId: row.type === EApproveType.SERVER_TAX_SOURCE || row.type === EApproveType.SERVER_EDIT_TAX_SOURCE ? row.serverId : undefined,
      actinveIndex: actinveIndex.value,
      step: row.step,
    },
    state: row?.extraInfo ? JSON.parse(String(row?.extraInfo)) : undefined,
  });
}

async function fetchData() {
  emit('update:loading', true);
  try {
    const { data, total: all } = await queryApproveTaskList({
      ...params,
      submitTimeFrom: dateRange.value ? dateRange.value[0] : undefined,
      submitTimeTo: dateRange.value ? dateRange.value[1] : undefined,
    });

    tableData.value = data;

    emit('update:total', all);
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  emit('update:loading', false);
}

async function fetchBusinessLineList() {
  try {
    businessLineList.value = await queryBusinessLineList();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size', 'statuses'].includes(key)) {
      params[key] = undefined;
    }
  }

  dateRange.value = [];
}

onMounted(() => {
  resetForm();
  fetchData();
  fetchBusinessLineList();
});

watch(
  () => props.pageParams,
  (val) => {
    if (val.page !== params.page) {
      params.page = val.page;
      fetchData();
    }
  },
  { deep: true },
);

</script>

<style lang="scss" scoped>
:deep(.normal-table) {
  .el-table__cell {
    @apply py-2 !important;
  }

  tbody {
    .el-table__row {
      &:last-child {
        td.el-table__cell {
          border-bottom: 1px solid var(--color-split-line) !important;
        }
      }
    }
  }
}
</style>

